import { useState } from "react";
import type { FolderSummary } from "@/lib/store";
import { ConfirmDialog } from "../../ConfirmDialog";

type Props = {
  folder: FolderSummary;
  collapsed: boolean;
  onToggle: () => void;
  onRename: (name: string) => void;
  onDelete: () => void;
  onDropChat: (chatId: string) => void;
};

export function FolderHeader({ folder, collapsed, onToggle, onRename, onDelete, onDropChat }: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(folder.name);
  const [dragOver, setDragOver] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [notEmptyOpen, setNotEmptyOpen] = useState(false);

  const submit = () => {
    const v = draft.trim();
    if (v && v !== folder.name) onRename(v);
    setEditing(false);
  };

  return (
    <div
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes("application/x-chat-id")) {
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
          setDragOver(true);
        }
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        const id = e.dataTransfer.getData("application/x-chat-id");
        setDragOver(false);
        if (id) { e.preventDefault(); onDropChat(id); }
      }}
      className={`group flex items-center gap-1 border-b px-2 py-1.5 text-xs transition-colors ${
        dragOver ? "bg-primary/15 ring-1 ring-primary/40" : "bg-muted/30"
      }`}
    >
      <button
        onClick={onToggle}
        className="flex flex-1 items-center gap-1.5 truncate text-left text-muted-foreground hover:text-foreground"
      >
        <i className={`fa-solid fa-chevron-${collapsed ? "right" : "down"} text-[10px] w-3`} />
        <i className="fa-solid fa-folder" />
        {editing ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={submit}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
              if (e.key === "Escape") { setDraft(folder.name); setEditing(false); }
            }}
            onClick={(e) => e.stopPropagation()}
            className="flex-1 rounded border bg-background px-1 py-0.5 text-xs outline-none focus:border-primary"
          />
        ) : (
          <span className="truncate font-medium">{folder.name}</span>
        )}
        <span className="ml-1 text-muted-foreground/60">{folder.conversationCount}</span>
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); setDraft(folder.name); setEditing(true); }}
        className="px-1 text-muted-foreground opacity-0 hover:text-foreground group-hover:opacity-100"
        title="Rename"
      >
        <i className="fa-solid fa-pen text-[10px]" />
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (folder.conversationCount > 0) setNotEmptyOpen(true);
          else setConfirming(true);
        }}
        className="px-1 text-muted-foreground opacity-0 hover:text-destructive group-hover:opacity-100"
        title={folder.conversationCount > 0 ? "Folder not empty" : "Delete folder"}
      >
        <i className="fa-solid fa-trash-can text-[10px]" />
      </button>
      {confirming && (
        <ConfirmDialog
          title={`Delete folder "${folder.name}"?`}
          confirmLabel="Delete"
          destructive
          onConfirm={() => { setConfirming(false); onDelete(); }}
          onCancel={() => setConfirming(false)}
        />
      )}
      {notEmptyOpen && (
        <ConfirmDialog
          title="Folder not empty"
          message={`"${folder.name}" still contains chats. Move or delete them first.`}
          confirmLabel="OK"
          cancelLabel=""
          onConfirm={() => setNotEmptyOpen(false)}
          onCancel={() => setNotEmptyOpen(false)}
        />
      )}
    </div>
  );
}
